/**
 * route.v2 — give every dashboard page its own URL.
 *
 * The v2 shell (PreviewHome) used to switch pages with plain React state, so
 * every page lived at the same address (dashboard.fancall.in). This maps each
 * page id to a clean path and keeps the active page in sync with the URL, so
 * bookmarking, sharing, refresh, and browser back/forward all work per page.
 *
 * Scope: page only. Date range / open detail modal stay in-session (not URL'd).
 */
import { useState, useEffect, useCallback } from 'react';

// id → URL path. Home is the root. Ids match the sidebar NAV ids.
export const PATH_BY_ID = {
  home:        '/',
  fans:        '/fans',
  creators:    '/creators',
  brands:      '/brands',
  money:       '/money',
  operations:  '/operations',
  acquisition: '/acquisition',
  audit:       '/audit',
  investor:    '/investor',
  relational:  '/relational',
  custom:      '/build',
  settings:    '/settings',
};

const ID_BY_PATH = Object.entries(PATH_BY_ID).reduce((acc, [id, path]) => {
  acc[path] = id;
  return acc;
}, {});

/** URL path for a page id (falls back to home). */
export function pathForId(id) {
  return PATH_BY_ID[id] || '/';
}

/** Page id for a URL path (trailing slashes ignored; unknown → home). */
export function idForPath(pathname) {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  return ID_BY_PATH[clean] || 'home';
}

/**
 * usePageRoute — returns { active, navigate }.
 *   active   : current page id, derived from window.location on first render
 *              and kept in sync with browser back/forward (popstate).
 *   navigate : (id) => push the page's path and switch to it.
 *
 * On mount it normalises the address bar (e.g. an unknown path or a trailing
 * slash) so the URL always reflects a real page.
 */
export function usePageRoute() {
  const [active, setActive] = useState(() =>
    idForPath(typeof window !== 'undefined' ? window.location.pathname : '/')
  );

  // Normalise the URL once on mount (unknown path → '/', '/money/' → '/money').
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const canonical = pathForId(idForPath(window.location.pathname));
    if (window.location.pathname !== canonical) {
      window.history.replaceState({ page: idForPath(canonical) }, '', canonical);
    }
    const onPop = () => setActive(idForPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((id) => {
    const path = pathForId(id);
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState({ page: id }, '', path);
    }
    setActive(id);
  }, []);

  return { active, navigate };
}
